import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useDmPatient } from '../../hooks/useDmPatient';
import { usePatientCard } from '../../hooks/usePatientCard';
import type { PatientPracticeBlock } from '../../services/patientCard';
import { useStatusCatalog } from '../../services/statusCatalog';
import { formatCreatedDate } from '../../utils/dates';
import { getStatusColor } from '../../utils/statusColors';

interface InfoTabProps {
    patientId: string;
}

function Section({
    title,
    children,
    defaultOpen = true
}: {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-xl border border-gray-200 bg-white">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-4"
            >
                <h4 className="text-base font-semibold text-gray-700">{title}</h4>
                <ChevronDownIcon
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                />
            </button>
            <div
                className={`grid transition-all duration-300 ease-out ${
                    open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
            >
                <div className="overflow-hidden">
                    <div className="px-5 pb-5">
                        <div className="h-px bg-gray-200 mb-5" />
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
    const display = value != null && value !== '' ? String(value) : '--';
    const isEmpty = value == null || value === '';
    return (
        <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm font-semibold text-[#6B7280]">{label}</span>
            <span
                className={`text-sm text-right ${isEmpty ? 'italic text-gray-400' : 'text-gray-800'}`}
            >
                {display}
            </span>
        </div>
    );
}

function HighlightField({
    label,
    value,
    emptyText = '--'
}: {
    label: string;
    value: string | null | undefined;
    emptyText?: string;
}) {
    const isEmpty = !value;
    const color = value ? getStatusColor(value) : '';
    return (
        <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm font-semibold text-[#6B7280]">{label}</span>
            <span className={`text-sm font-medium ${isEmpty ? 'italic text-gray-400' : color}`}>
                {value ?? emptyText}
            </span>
        </div>
    );
}

function DateField({ label, value }: { label: string; value: string | null | undefined }) {
    const formatted = value ? formatCreatedDate(value) : null;
    return <HighlightField label={label} value={formatted} />;
}

function NoteField({ label, value }: { label: string; value: string | null | undefined }) {
    if (!value) return <Field label={label} value={null} />;
    return (
        <div>
            <span className="text-sm font-semibold text-[#6B7280] block mb-2">{label}</span>
            <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                {value}
            </div>
        </div>
    );
}

function safeHref(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' || url.protocol === 'http:' ? value : undefined;
    } catch {
        return undefined;
    }
}

function LinkField({ label, value }: { label: string; value: string | null | undefined }) {
    const href = safeHref(value);
    if (!href) return <Field label={label} value={null} />;
    return (
        <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm font-semibold text-[#6B7280]">{label}</span>
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-700 hover:underline"
            >
                Open
            </a>
        </div>
    );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
    return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function nonBlank(value: string | null | undefined): string | null {
    return value?.trim() ? value : null;
}

function joinParts(parts: (string | null | undefined)[], separator: string): string | null {
    const present = parts.filter((part): part is string => !!nonBlank(part));
    return present.length ? present.join(separator) : null;
}

function PracticeSection({ practice }: { practice: PatientPracticeBlock | undefined }) {
    return (
        <Section title="Practice">
            <FieldGrid>
                <HighlightField
                    label="Scanner"
                    value={nonBlank(practice?.scanner)}
                    emptyText="Not recorded"
                />
                <Field label="Spark account" value={nonBlank(practice?.sparkAcct)} />
                <Field label="Invisalign account" value={nonBlank(practice?.invisalignAcct)} />
                <Field label="Practice name" value={nonBlank(practice?.name)} />
                <Field
                    label="Doctor"
                    value={joinParts([practice?.drFirstName, practice?.drLastName], ' ')}
                />
                <Field label="Doctor email" value={nonBlank(practice?.drEmail)} />
                <Field label="Practice phone" value={nonBlank(practice?.phone)} />
                <Field label="Practice email" value={nonBlank(practice?.email)} />
                <Field
                    label="Location"
                    value={joinParts([practice?.city, practice?.state, practice?.country], ', ')}
                />
            </FieldGrid>
        </Section>
    );
}

function InfoTab({ patientId }: InfoTabProps) {
    const { data, isLoading, error, refetch } = usePatientCard(patientId);
    // The live interval comes from Dental Monitoring; the Clinical Instructions field is the SDI/RDI plan.
    const dmLinked = data?.monitoring?.dmId != null;
    const { data: dmPatient, isLoading: dmLoading } = useDmPatient(
        dmLinked ? patientId : undefined
    );
    const liveInterval =
        dmLinked && dmLoading
            ? 'Loading…'
            : dmPatient?.intervalDays != null
              ? `Every ${dmPatient.intervalDays} day${dmPatient.intervalDays === 1 ? '' : 's'}`
              : null;
    const catalog = useStatusCatalog();

    if (isLoading) {
        return (
            <div className="flex-1 overflow-auto p-6">
                <div className="flex flex-col gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
                <div className="text-xs text-red-500">Failed to load patient data</div>
                <button
                    onClick={() => refetch()}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-auto p-6">
            <div className="flex flex-col gap-4">
                {/* Case Profile */}
                <Section title="Case Profile">
                    <FieldGrid>
                        <HighlightField label="Status" value={data.status?.caseStatus} />
                        <HighlightField
                            label="Internal status"
                            value={
                                data.status?.internalCaseStatus
                                    ? catalog.display(data.status.internalCaseStatus)
                                    : undefined
                            }
                        />
                        <HighlightField
                            label="Action required"
                            value={data.status?.actionRequired}
                        />
                        <Field label="Case type" value={data.treatment?.caseType} />
                        <Field label="Sub case type" value={data.treatment?.subCaseType} />
                        <Field label="Manufacturer" value={data.treatment?.manufacturer} />
                        <Field
                            label="Manufacturer case ID"
                            value={data.treatment?.manufacturerCaseId}
                        />
                        <Field label="Aligner option" value={data.treatment?.alignerOption} />
                        <Field label="Case acknowledge" value={data.treatment?.caseAcknowledge} />
                        <Field
                            label="Classification"
                            value={data.treatment?.patientClassification}
                        />
                        <Field label="Complexity" value={data.treatment?.caseComplexity} />
                        <Field label="Design type" value={data.treatment?.designType} />
                        <Field label="Expedite" value={data.treatment?.expedite} />
                    </FieldGrid>
                </Section>

                <PracticeSection practice={data.practice} />

                {/* Treatment Plan */}
                <Section title="Treatment Plan">
                    <FieldGrid>
                        <Field label="Duration" value={data.treatment?.treatmentDuration} />
                        <Field
                            label="Est. duration"
                            value={data.treatment?.estimatedTreatmentDuration}
                        />
                        <HighlightField label="Fees" value={data.treatment?.suggestedFees} />
                        <Field label="Current aligner" value={data.monitoring?.currentAligner} />
                        <Field label="Total aligners" value={data.monitoring?.totalAligners} />
                        <Field label="Refinements" value={data.treatment?.refinementsCount} />
                        <Field label="Version" value={data.status?.version} />
                        <Field label="Orders" value={data.status?.orders} />
                        <Field label="OrthoPulse" value={data.orthopulse?.recommendation} />
                        <Field label="OrthoPulse status" value={data.orthopulse?.status} />
                        <Field label="OrthoPulse price" value={data.orthopulse?.price} />
                    </FieldGrid>
                </Section>

                {/* Timeline */}
                <Section title="Timeline">
                    <FieldGrid>
                        <DateField
                            label="Next appointment"
                            value={data.dates?.nextAppointmentDate}
                        />
                        <DateField label="Submission" value={data.dates?.submissionDate} />
                        <DateField label="Approval" value={data.dates?.approvalDate} />
                        <DateField label="Scheduled start" value={data.dates?.scheduledStartDate} />
                        <DateField label="Photos" value={data.dates?.photosDate} />
                        <DateField label="Last scan" value={data.dates?.scansDate} />
                        <Field label="Next appt reason" value={data.dates?.nextAppointmentReason} />
                        <DateField label="Spark submitted" value={data.dates?.sparkSubmittedDate} />
                    </FieldGrid>
                </Section>

                {/* Clinical Instructions */}
                <Section title="Clinical Instructions">
                    <FieldGrid>
                        <Field label="Left elastics" value={data.ortho?.leftElastics} />
                        <Field label="Right elastics" value={data.ortho?.rightElastics} />
                        <Field label="Left size" value={data.ortho?.leftElasticsSize} />
                        <Field label="Right size" value={data.ortho?.rightElasticsSize} />
                        <Field label="Aligner wear" value={data.ortho?.alignerWear} />
                        <Field
                            label="Scan frequency (planned)"
                            value={data.monitoring?.dmScanFrequency}
                        />
                        <Field label="Aligner count" value={data.ortho?.provideAlignerCount} />
                        <Field label="Next appt at aligner" value={data.ortho?.nextApptAtAligner} />
                    </FieldGrid>
                </Section>

                {/* Dental Monitoring */}
                <Section title="Dental Monitoring">
                    <FieldGrid>
                        <HighlightField label="Status" value={data.monitoring?.monitoringStatus} />
                        <Field label="Scan frequency" value={liveInterval} />
                        <Field
                            label="App activation"
                            value={data.monitoring?.dmAppActivationStatus}
                        />
                        <Field label="Quickstart type" value={data.monitoring?.quickstartType} />
                        <Field
                            label="Activation count"
                            value={data.monitoring?.dmLinkActivationCount}
                        />
                        <Field label="DM ID" value={data.monitoring?.dmId} />
                        <Field label="iTero scan ID" value={data.monitoring?.iteroScanId} />
                        <Field label="DM profile ID" value={data.monitoring?.dmProfileId} />
                    </FieldGrid>
                </Section>

                {/* Notes & Goals */}
                <Section title="Notes & Goals">
                    <div className="flex flex-col gap-4">
                        <NoteField label="Treatment goals" value={data.notes?.treatmentGoals} />
                        <NoteField label="Doctor goals" value={data.notes?.doctorGoals} />
                        <NoteField label="DM notes" value={data.notes?.dmNotes} />
                        <NoteField label="SDI notes" value={data.clinical?.sdiNotes} />
                        <NoteField label="Refinement goal" value={data.notes?.refinementGoal} />
                        <NoteField label="Refinement notes" value={data.notes?.refinementNotes} />
                        <NoteField
                            label="Refinement rejection"
                            value={data.notes?.refinementRejectionReason}
                        />
                        <NoteField label="Retention notes" value={data.notes?.retentionNotes} />
                        <NoteField
                            label="Retention rejection"
                            value={data.notes?.retentionRejectionReason}
                        />
                        <NoteField label="Future notes" value={data.ortho?.futureNotes} />
                        <NoteField label="VCA team info" value={data.ortho?.vcaTeamInfo} />
                        <NoteField
                            label="Doc preferences"
                            value={data.ortho?.treatingDocPreferences}
                        />
                        <NoteField label="Additional info" value={data.treatment?.additionalInfo} />
                    </div>
                </Section>

                {/* Resources */}
                <Section title="Resources">
                    <FieldGrid>
                        <LinkField label="Manufacturer" value={data.treatment?.manufacturerLink} />
                        <LinkField label="Dental Monitoring" value={data.monitoring?.dmLink} />
                        <LinkField label="CBCT upload" value={data.clinical?.cbctUploadLink} />
                        <LinkField label="DX report" value={data.clinical?.dxReportLink} />
                        <LinkField label="EasyRx" value={data.clinical?.easyRxLink} />
                        <Field label="Ortho item ID" value={data.boards?.orthoItemId} />
                    </FieldGrid>
                </Section>
            </div>
        </div>
    );
}

export { InfoTab };
